import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { label } from "../../lib/format";
import { Field, PageHeader, Td, Th } from "../../components/common/UI";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const BLANK = { code: "", name: "", lgu_type: "municipality", region: "", province: "", legislative_body_name: "", term_name: "2025-2028", term_start: "2025-06-30", term_end: "2028-06-30", admin_email: "", admin_full_name: "", admin_password: "" };

export default function PlatformLgus() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState(null);
  const load = () => api.get("/platform/lgus").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.post("/platform/lgus", { ...f, code: f.code.toUpperCase(), timezone: "Asia/Manila" });
      toast.success("LGU tenant created with default roles, workflow and numbering");
      setF(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggle = (l) => api.put(`/platform/lgus/${l.id}/status`, { is_active: !l.is_active }).then(load);
  const inp = (k, l, type = "text") => <Field label={l}><input type={type} className="otms-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`lgu-create-${k}`} /></Field>;
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Platform" title="LGU Tenants" subtitle="Platform administrators manage tenants but have no access to tenant legislative records."
        actions={<Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => setF({ ...BLANK })} data-testid="add-lgu-button"><Plus className="mr-1 h-4 w-4" />New LGU</Button>} />
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[720px]" data-testid="lgus-table">
          <thead><tr><Th>Code</Th><Th>Name</Th><Th>Type</Th><Th>Legislative body</Th><Th>Users</Th><Th>Active</Th></tr></thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id}>
                <Td className="font-mono">{l.code}</Td><Td className="font-medium">{l.name}</Td><Td>{label(l.lgu_type)}</Td><Td>{l.legislative_body_name}</Td><Td>{l.user_count}</Td>
                <Td><input type="checkbox" checked={l.is_active} onChange={() => toggle(l)} data-testid={`lgu-active-${l.code}`} /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="lgu-create-dialog">
          <DialogHeader><DialogTitle>New LGU tenant</DialogTitle></DialogHeader>
          {f && (
            <div className="grid gap-3 md:grid-cols-2">
              {inp("code", "Code (A-Z, 0-9)")}{inp("name", "Official name")}
              <Field label="Type"><select className="otms-input" value={f.lgu_type} onChange={(e) => setF({ ...f, lgu_type: e.target.value })} data-testid="lgu-create-type">{["barangay", "municipality", "city", "province"].map((t) => <option key={t} value={t}>{label(t)}</option>)}</select></Field>
              {inp("legislative_body_name", "Legislative body")}{inp("region", "Region")}{inp("province", "Province")}
              {inp("term_name", "Initial term")}{inp("term_start", "Term start", "date")}{inp("term_end", "Term end", "date")}
              {inp("admin_full_name", "LGU admin name")}{inp("admin_email", "LGU admin email")}{inp("admin_password", "LGU admin password", "password")}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setF(null)}>Cancel</Button><Button className="bg-sky-600 hover:bg-sky-700" onClick={save} data-testid="lgu-create-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
